import { Platform } from "react-native";

import { ensureMapsLoaded } from "@/lib/maps-loader";

const GOOGLE_MAPS_API_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";

export interface Prediction {
    description: string;
    placeId: string;
}

export interface PlaceDetails {
    address: string;
    lat: number;
    lng: number;
}

type PlacesAPI = {
    AutocompleteService: new () => unknown;
    PlacesService: new (el: HTMLElement) => unknown;
};

function getPlacesAPI(): PlacesAPI | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }
    const google = (window as unknown as { google?: { maps?: { places?: PlacesAPI } } }).google;
    return google?.maps?.places;
}

async function fetchPredictionsWeb(input: string): Promise<Prediction[]> {
    await ensureMapsLoaded();
    const places = getPlacesAPI();
    if (!places) {
        return [];
    }
    const svc = new places.AutocompleteService() as {
        getPlacePredictions: (
            req: { input: string; types: string[]; componentRestrictions: { country: string } },
            cb: (results: Array<{ description: string; place_id: string }> | null) => void
        ) => void;
    };
    return new Promise<Prediction[]>((resolve) => {
        svc.getPlacePredictions(
            { input, types: ["address"], componentRestrictions: { country: "us" } },
            (results) => {
                resolve(
                    (results ?? []).map((p) => ({
                        description: p.description,
                        placeId: p.place_id
                    }))
                );
            }
        );
    });
}

let webPlacesServiceEl: HTMLDivElement | null = null;
let webPlacesService: unknown | null = null;

async function fetchPlaceDetailsWeb(placeId: string): Promise<PlaceDetails | null> {
    await ensureMapsLoaded();
    const places = getPlacesAPI();
    if (!places) {
        return null;
    }
    if (!webPlacesService) {
        webPlacesServiceEl = document.createElement("div");
        webPlacesService = new places.PlacesService(webPlacesServiceEl);
    }
    const svc = webPlacesService as {
        getDetails: (
            req: { placeId: string; fields: string[] },
            cb: (result: unknown, status: string) => void
        ) => void;
    };
    return new Promise<PlaceDetails | null>((resolve) => {
        svc.getDetails(
            { placeId, fields: ["formatted_address", "geometry"] },
            (result: unknown, status: string) => {
                if (status !== "OK" || !result) {
                    resolve(null);
                    return;
                }
                const r = result as {
                    formatted_address?: string;
                    geometry?: { location?: { lat: () => number; lng: () => number } };
                };
                if (!r.geometry?.location) {
                    resolve(null);
                    return;
                }
                resolve({
                    address: r.formatted_address ?? "",
                    lat: r.geometry.location.lat(),
                    lng: r.geometry.location.lng()
                });
            }
        );
    });
}

async function fetchPredictionsNative(input: string): Promise<Prediction[]> {
    if (!GOOGLE_MAPS_API_KEY) {
        return [];
    }
    try {
        const url = `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(
            input
        )}&types=address&components=country:us&key=${GOOGLE_MAPS_API_KEY}`;
        const res = await fetch(url);
        const data = (await res.json()) as {
            predictions?: Array<{ description: string; place_id: string }>;
        };
        return (data.predictions ?? []).map((p) => ({
            description: p.description,
            placeId: p.place_id
        }));
    } catch {
        return [];
    }
}

async function fetchPlaceDetailsNative(placeId: string): Promise<PlaceDetails | null> {
    if (!GOOGLE_MAPS_API_KEY) {
        return null;
    }
    try {
        const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${encodeURIComponent(
            placeId
        )}&fields=formatted_address,geometry&key=${GOOGLE_MAPS_API_KEY}`;
        const res = await fetch(url);
        const data = (await res.json()) as {
            result?: {
                formatted_address?: string;
                geometry?: { location?: { lat: number; lng: number } };
            };
        };
        const result = data.result;
        if (!result?.geometry?.location) {
            return null;
        }
        return {
            address: result.formatted_address ?? "",
            lat: result.geometry.location.lat,
            lng: result.geometry.location.lng
        };
    } catch {
        return null;
    }
}

export const hasPlacesApiKey = (): boolean => Boolean(GOOGLE_MAPS_API_KEY);

export const fetchPredictions = (input: string): Promise<Prediction[]> =>
    Platform.OS === "web" ? fetchPredictionsWeb(input) : fetchPredictionsNative(input);

export const fetchPlaceDetails = (placeId: string): Promise<PlaceDetails | null> =>
    Platform.OS === "web" ? fetchPlaceDetailsWeb(placeId) : fetchPlaceDetailsNative(placeId);
